import React, { useState } from 'react';
import { 
  Download, 
  Eye, 
  Palette, 
  Grid, 
  Layers, 
  BookOpen, 
  Check, 
  Copy, 
  ExternalLink, 
  Sparkles, 
  Sun, 
  Moon, 
  ArrowRight,
  Smartphone,
  Maximize2
} from 'lucide-react';

export default function LogoPage() {
  const [selectedSymbol, setSelectedSymbol] = useState('gold'); // 'gold' or 'charcoal'
  const [theme, setTheme] = useState('light'); // 'light', 'dark', 'emerald'
  const [layout, setLayout] = useState('horizontal'); // 'horizontal', 'vertical', 'symbol'
  const [logoSize, setLogoSize] = useState(100); // percentage
  const [copiedColor, setCopiedColor] = useState(null);

  const symbols = {
    charcoal: {
      url: "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f961e663-836a-4a16-bedc-b6b4579575c7.png",
      name: "Koksgrå / Jordforbindelse",
      description: "En dyb, rolig koksgrå farve, der repræsenterer det jordiske, det simple og det professionelle."
    },
    gold: {
      url: "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image.png",
      name: "Guld / Sjælelys",
      description: "En varm guld/bronze-tone, der repræsenterer det spirituelle, oplysning og hjertets intelligens."
    }
  };

  const colors = [
    { name: "Skovgrøn (Primær)", hex: "#004f3b", rgb: "0, 79, 59", cmyk: "90, 30, 70, 40", use: "Baggrunde, knapper, dyb kontrast og brand-identitet" },
    { name: "Sjæleguld", hex: "#c5a059", rgb: "197, 160, 89", cmyk: "20, 35, 70, 5", use: "Logo-symbol, detaljer, highlights og spirituel glød" },
    { name: "Mørk Koksgrå", hex: "#1c1917", rgb: "28, 25, 23", cmyk: "70, 65, 65, 80", use: "Tekst, mørke baggrunde, fodnote og stærk kontrast" },
    { name: "Varm Sten", hex: "#faf9f6", rgb: "250, 249, 246", cmyk: "2, 2, 4, 0", use: "Lyse baggrunde, sektioner, luftighed og ro" },
    { name: "Ren Hvid", hex: "#ffffff", rgb: "255, 255, 255", cmyk: "0, 0, 0, 0", use: "Kort, renhed, kontraster og negativ plads" }
  ];

  const handleCopyColor = (hex) => {
    navigator.clipboard.writeText(hex);
    setCopiedColor(hex);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  // Function to download all primary brand assets
  const handleDownloadAll = () => {
    const assets = [
      { url: "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image.png", name: "mette-brudvig-logo-guld-horisontalt.png" },
      { url: "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ddf45d18-3162-4a49-b537-5eba838b3e4f.png", name: "mette-brudvig-logo-koksgraa-horisontalt.png" },
      { url: "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/8b5bf536-c411-474d-bbd3-0d9bb5bbc475.png", name: "mette-brudvig-symbol-guld.png" },
      { url: "https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f961e663-836a-4a16-bedc-b6b4579575c7.png", name: "mette-brudvig-symbol-koksgraa.png" }
    ];

    assets.forEach((asset, index) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = asset.url;
        link.download = asset.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 400); // Stagger downloads to prevent browser blocking
    });
  };

  // Function to export a beautiful HTML Brand Guide
  const handleExportBrandGuide = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="da">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Mette Brudvig - Brand Guidelines & Style Guide</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');
        
        :root {
            --color-emerald: #004f3b;
            --color-gold: #c5a059;
            --color-charcoal: #1c1917;
            --color-stone: #faf9f6;
            --color-white: #ffffff;
        }

        body {
            font-family: 'Plus Jakarta Sans', sans-serif;
            background-color: var(--color-stone);
            color: var(--color-charcoal);
            margin: 0;
            padding: 0;
            line-height: 1.6;
        }

        header {
            background-color: var(--color-emerald);
            color: var(--color-white);
            padding: 80px 40px;
            text-align: center;
            border-bottom: 5px solid var(--color-gold);
        }

        header h1 {
            font-family: 'Playfair Display', serif;
            font-size: 3.5rem;
            margin: 0 0 10px 0;
            font-weight: 300;
            letter-spacing: -0.02em;
        }

        header p {
            font-size: 1.2rem;
            font-weight: 300;
            opacity: 0.9;
            margin: 0;
            letter-spacing: 0.05em;
        }

        .container {
            max-width: 1000px;
            margin: 60px auto;
            padding: 0 20px;
        }

        section {
            background: var(--color-white);
            border-radius: 16px;
            padding: 40px;
            margin-bottom: 40px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.02);
            border: 1px solid rgba(0,0,0,0.03);
        }

        h2 {
            font-family: 'Playfair Display', serif;
            font-size: 2rem;
            color: var(--color-emerald);
            margin-top: 0;
            border-bottom: 1px solid #eee;
            padding-bottom: 15px;
        }

        .grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 30px;
            margin-top: 30px;
        }

        .logo-card {
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 20px;
            text-align: center;
            background: #fcfcfc;
        }

        .logo-preview {
            height: 150px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f3f3f3;
            border-radius: 8px;
            margin-bottom: 15px;
            overflow: hidden;
            padding: 20px;
        }

        .logo-preview img {
            max-height: 100%;
            max-width: 100%;
            object-contain: fit;
        }

        .logo-card h3 {
            font-family: 'Playfair Display', serif;
            margin: 10px 0 5px 0;
            font-size: 1.2rem;
        }

        .logo-card p {
            font-size: 0.85rem;
            color: #666;
            margin: 0 0 15px 0;
        }

        .btn {
            display: inline-block;
            background: var(--color-emerald);
            color: var(--color-white);
            text-decoration: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 0.85rem;
            font-weight: 600;
            transition: background 0.2s;
        }

        .btn:hover {
            background: #003628;
        }

        .color-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 20px;
            margin-top: 30px;
        }

        .color-card {
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid #eee;
            box-shadow: 0 2px 10px rgba(0,0,0,0.01);
        }

        .color-swatch {
            height: 120px;
        }

        .color-info {
            padding: 15px;
            background: #fff;
        }

        .color-info h4 {
            margin: 0 0 5px 0;
            font-size: 1rem;
        }

        .color-info p {
            font-size: 0.8rem;
            margin: 3px 0;
            font-family: monospace;
            color: #555;
        }

        .font-example {
            font-size: 3rem;
            line-height: 1.2;
            margin-bottom: 10px;
        }

        .font-serif-ex {
            font-family: 'Playfair Display', serif;
        }

        .font-sans-ex {
            font-family: 'Plus Jakarta Sans', sans-serif;
        }

        footer {
            text-align: center;
            padding: 40px;
            color: #888;
            font-size: 0.9rem;
            border-top: 1px solid #eee;
        }

        @media print {
            body {
                background: white;
            }
            section {
                box-shadow: none;
                border: none;
                page-break-inside: avoid;
            }
            .btn {
                display: none;
            }
        }
    </style>
</head>
<body>

    <header>
        <h1>Mette Brudvig</h1>
        <p>BRAND GUIDELINES & STYLE GUIDE</p>
    </header>

    <div class="container">
        <section>
            <h2>Brand Filosofi & Vision</h2>
            <p>Mette Brudvigs visuelle identitet forener det spirituelle, det jordiske og det enkle. Det er et udtryk for <strong>Hjertets Intelligens</strong> – en dyb, indre visdom, der her formidles gennem et rent, nordisk og tidløst design.</p>
            <p><strong>Lotus & Livstræ-symbolet</strong> repræsenterer denne synergi: Lotusblomsten for spirituel opvågning og renhed, og Livstræet for dybe rødder, vækst og jordforbindelse.</p>
        </section>

        <section>
            <h2>Logo-aktiver</h2>
            <p>Her kan du downloade de officielle logo-filer direkte fra vores brand-server. Alle filer er i højopløseligt PNG-format med transparent baggrund.</p>
            
            <div class="grid">
                <div class="logo-card">
                    <div class="logo-preview">
                        <img src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image.png" alt="Logo Guld Horisontalt">
                    </div>
                    <h3>Guld Logo (Horisontalt)</h3>
                    <p>Primært logo til lyse baggrunde, website header og brevpapir.</p>
                    <a href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image.png" target="_blank" class="btn">Download PNG</a>
                </div>

                <div class="logo-card">
                    <div class="logo-preview" style="background: #1c1917;">
                        <img src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ddf45d18-3162-4a49-b537-5eba838b3e4f.png" alt="Logo Koksgrå Horisontalt">
                    </div>
                    <h3>Koksgrå Logo (Horisontalt)</h3>
                    <p>Alternativt logo til lyse/mørke baggrunde og minimalistisk tryk.</p>
                    <a href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ddf45d18-3162-4a49-b537-5eba838b3e4f.png" target="_blank" class="btn">Download PNG</a>
                </div>

                <div class="logo-card">
                    <div class="logo-preview">
                        <img src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/8b5bf536-c411-474d-bbd3-0d9bb5bbc475.png" alt="Symbol Guld">
                    </div>
                    <h3>Lotus & Livstræ Symbol (Guld)</h3>
                    <p>Rent symbol til sociale medier, ikoner og præg.</p>
                    <a href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/8b5bf536-c411-474d-bbd3-0d9bb5bbc475.png" target="_blank" class="btn">Download PNG</a>
                </div>

                <div class="logo-card">
                    <div class="logo-preview">
                        <img src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f961e663-836a-4a16-bedc-b6b4579575c7.png" alt="Symbol Koksgrå">
                    </div>
                    <h3>Lotus & Livstræ Symbol (Koksgrå)</h3>
                    <p>Rent symbol i koksgrå til hverdagsbrug og stempler.</p>
                    <a href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f961e663-836a-4a16-bedc-b6b4579575c7.png" target="_blank" class="btn">Download PNG</a>
                </div>
            </div>
        </section>

        <section>
            <h2>Farvepalette</h2>
            <p>Farverne er nøje udvalgt til at afspejle den dybe skovgrønne ro, den spirituelle guldglød og den jordnære koksgrå stabilitet.</p>
            
            <div class="color-grid">
                <div class="color-card">
                    <div class="color-swatch" style="background-color: #004f3b;"></div>
                    <div class="color-info">
                        <h4>Skovgrøn</h4>
                        <p>HEX: #004f3b</p>
                        <p>RGB: 0, 79, 59</p>
                        <p>CMYK: 90, 30, 70, 40</p>
                    </div>
                </div>

                <div class="color-card">
                    <div class="color-swatch" style="background-color: #c5a059;"></div>
                    <div class="color-info">
                        <h4>Sjæleguld</h4>
                        <p>HEX: #c5a059</p>
                        <p>RGB: 197, 160, 89</p>
                        <p>CMYK: 20, 35, 70, 5</p>
                    </div>
                </div>

                <div class="color-card">
                    <div class="color-swatch" style="background-color: #1c1917;"></div>
                    <div class="color-info">
                        <h4>Mørk Koksgrå</h4>
                        <p>HEX: #1c1917</p>
                        <p>RGB: 28, 25, 23</p>
                        <p>CMYK: 70, 65, 65, 80</p>
                    </div>
                </div>

                <div class="color-card">
                    <div class="color-swatch" style="background-color: #faf9f6;"></div>
                    <div class="color-info">
                        <h4>Varm Sten</h4>
                        <p>HEX: #faf9f6</p>
                        <p>RGB: 250, 249, 246</p>
                        <p>CMYK: 2, 2, 4, 0</p>
                    </div>
                </div>
            </div>
        </section>

        <section>
            <h2>Typografi</h2>
            <div class="grid">
                <div>
                    <h3>Overskrifter (Display)</h3>
                    <div class="font-example font-serif-ex">Playfair Display</div>
                    <p>En klassisk og elegant serif-skrifttype, der anvendes til primære overskrifter (H1, H2) for at give et sofistikeret og tidløst udtryk.</p>
                </div>
                <div>
                    <h3>Brødtekst (Body)</h3>
                    <div class="font-example font-sans-ex" style="font-size: 2rem;">Plus Jakarta Sans</div>
                    <p>En moderne, letlæselig sans-serif skrifttype, der sikrer høj læsbarhed på skærme og i mindre tekststørrelser.</p>
                </div>
            </div>
        </section>
    </div>

    <footer>
        <p>&copy; 2026 Mette Brudvig. Alle rettigheder forbeholdes.</p>
        <p>Style Guide genereret automatisk fra Mette Brudvigs Brand Kit.</p>
    </footer>

</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mette-brudvig-brand-guide.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activeSymbolUrl = symbols[selectedSymbol].url;

  // Theme background and text classes
  const getThemeClasses = () => {
    switch (theme) {
      case 'dark':
        return 'bg-stone-900 text-white border-stone-800';
      case 'emerald':
        return 'bg-[#004f3b] text-white border-emerald-900';
      case 'light':
      default:
        return 'bg-white text-stone-900 border-stone-100';
    }
  };

  return (
    <div className="bg-[#faf9f6] min-h-screen">
      {/* Hero Header */}
      <section className="pt-[150px] md:pt-[170px] pb-16 md:pb-24 px-6 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto space-y-6">
          <div className="inline-flex items-center space-x-2 bg-emerald-50 text-[#004f3b] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase">
            <Sparkles size={14} />
            <span>Brand Identity & Logo Kit</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-serif text-stone-900 tracking-tight">
            Mette Brudvig
          </h1>
          <p className="text-lg md:text-xl text-stone-600 font-light leading-relaxed">
            Et visuelt udtryk skabt med fokus på det spirituelle, det jordiske og det simple.
            Her kan du udforske, tilpasse og downloade dit nye logo.
          </p>
        </div>
      </section>

      {/* Design Concept Explanation */}
      <section className="py-12 px-6 max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
        <div className="bg-white p-8 rounded-xl border border-stone-100 shadow-sm space-y-4">
          <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center text-[#004f3b]">
            <span className="font-serif text-xl font-bold">A</span>
          </div>
          <h3 className="text-lg font-serif font-semibold text-stone-900">Navnet som Bærende</h3>
          <p className="text-stone-600 font-light text-sm leading-relaxed">
            Dit navn er det absolutte centrum. Skrifttypen er en klassisk, sofistikeret serif, der udstråler autoritet, ro og tidløs elegance. Det skal ikke være et "mesterværk" af kompleksitet – det skal bare se hamrende godt ud.
          </p>
        </div>

        <div className="bg-white p-8 rounded-xl border border-stone-100 shadow-sm space-y-4">
          <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
            <Layers size={22} />
          </div>
          <h3 className="text-lg font-serif font-semibold text-stone-900">Lotus & Livstræ</h3>
          <p className="text-stone-600 font-light text-sm leading-relaxed">
            Symbolet forener lotusblomsten (buddhistisk spiritualitet, renhed og opvågning) med livstræet (jordforbindelse, vækst og dybe rødder). En perfekt synergi mellem det himmelske og det jordiske.
          </p>
        </div>

        <div className="bg-white p-8 rounded-xl border border-stone-100 shadow-sm space-y-4">
          <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center text-stone-600">
            <Sun size={22} />
          </div>
          <h3 className="text-lg font-serif font-semibold text-stone-900">Cool, Let & Simpelt</h3>
          <p className="text-stone-600 font-light text-sm leading-relaxed">
            Ingen tunge skygger eller overflødige detaljer. Den fine stregtegning (line art) sikrer et luftigt og moderne udtryk, der fungerer perfekt på både mobil, print, sociale medier og visitkort.
          </p>
        </div>
      </section>

      {/* Interactive Logo Playground */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl border border-stone-100 shadow-xl overflow-hidden grid lg:grid-cols-12">
          
          {/* Controls Sidebar */}
          <div className="lg:col-span-4 p-8 md:p-10 border-r border-stone-100 bg-stone-50/50 space-y-8">
            <div>
              <h2 className="text-xl font-serif font-bold text-stone-900 mb-2">Logo-værktøjskasse</h2>
              <p className="text-xs text-stone-500">Leg med opsætningen og se, hvordan logoet tilpasser sig forskellige medier.</p>
            </div>

            {/* 1. Choose Symbol Color */}
            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider font-bold text-stone-400 block">1. Vælg Symbolfarve</label>
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => setSelectedSymbol('gold')}
                  className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border text-xs font-medium transition-all ${selectedSymbol === 'gold' ? 'bg-amber-50 border-amber-300 text-amber-900 shadow-sm font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                >
                  <div className="w-3.5 h-3.5 rounded-full bg-[#c5a059]" />
                  <span>Sjæleguld</span>
                </button>
                <button 
                  onClick={() => setSelectedSymbol('charcoal')}
                  className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border text-xs font-medium transition-all ${selectedSymbol === 'charcoal' ? 'bg-stone-100 border-stone-400 text-stone-900 shadow-sm font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                >
                  <div className="w-3.5 h-3.5 rounded-full bg-stone-600" />
                  <span>Koksgrå</span>
                </button>
              </div>
            </div>

            {/* 2. Choose Layout */}
            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider font-bold text-stone-400 block">2. Logo-opsætning</label>
              <div className="space-y-2">
                {[
                  { id: 'horizontal', label: 'Horisontalt (Standard)', desc: 'Perfekt til hjemmeside-header og breve' },
                  { id: 'vertical', label: 'Vertikalt / Centreret', desc: 'Flot til visitkort, poser og stempler' },
                  { id: 'symbol', label: 'Kun Symbol', desc: 'Til Instagram-profilbillede og favicon' }
                ].map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLayout(l.id)}
                    className={`w-full text-left p-3 rounded-lg border text-xs transition-all flex justify-between items-center ${layout === l.id ? 'bg-stone-900 border-stone-900 text-white shadow-md' : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'}`}
                  >
                    <div>
                      <p className="font-semibold">{l.label}</p>
                      <p className={`text-[10px] mt-0.5 ${layout === l.id ? 'text-stone-300' : 'text-stone-400'}`}>{l.desc}</p>
                    </div>
                    {layout === l.id && <Check size={14} />}
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Choose Background Theme */}
            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider font-bold text-stone-400 block">3. Baggrundsfarve</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'light', label: 'Lys', icon: <Sun size={14} />, colorBg: 'bg-white border-stone-200' },
                  { id: 'dark', label: 'Mørk', icon: <Moon size={14} />, colorBg: 'bg-stone-900 border-stone-800' },
                  { id: 'emerald', label: 'Skovgrøn', icon: <Palette size={14} />, colorBg: 'bg-[#004f3b] border-emerald-900' }
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t.id)}
                    className={`flex flex-col items-center justify-center py-3 rounded-lg border text-[10px] font-medium transition-all ${theme === t.id ? 'ring-2 ring-emerald-600 ring-offset-2 font-bold' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                  >
                    {t.icon}
                    <span className="mt-1">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 4. Logo Size Slider */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-xs uppercase tracking-wider font-bold text-stone-400">4. Størrelse</label>
                <span className="text-xs font-mono text-stone-500">{logoSize}%</span>
              </div>
              <input 
                type="range" 
                min="50" 
                max="150" 
                value={logoSize} 
                onChange={(e) => setLogoSize(Number(e.target.value))}
                className="w-full accent-stone-800 cursor-pointer"
              />
            </div>
          </div>

          {/* Interactive Preview Canvas */}
          <div className="lg:col-span-8 p-8 md:p-16 flex flex-col justify-between items-center min-h-[450px] md:min-h-[550px] relative transition-colors duration-500 bg-stone-50">
            
            {/* Canvas Header Info */}
            <div className="w-full flex justify-between items-center text-xs text-stone-400 mb-8 border-b border-stone-200/60 pb-4">
              <span className="font-mono uppercase tracking-widest">Forhåndsvisning</span>
              <span className="italic">Klik på "Download" for at hente filen</span>
            </div>

            {/* The Logo Container */}
            <div 
              className={`p-16 rounded-2xl border shadow-lg transition-all duration-500 flex items-center justify-center max-w-full overflow-hidden ${getThemeClasses()}`}
              style={{ 
                transform: `scale(${logoSize / 100})`,
                maxWidth: '90%'
              }}
            >
              {layout === 'horizontal' && (
                <div className="flex items-center space-x-5 text-left">
                  <div className="w-16 h-16 flex-shrink-0">
                    <img 
                      src={activeSymbolUrl} 
                      alt="Mette Brudvig Symbol" 
                      className={`w-full h-full object-contain mix-blend-multiply ${theme !== 'light' ? 'brightness-110' : ''}`}
                    />
                  </div>
                  <div className="flex flex-col">
                    <h2 className="text-3xl md:text-4xl font-serif tracking-tight leading-none">
                      Mette Brudvig
                    </h2>
                    <p className={`text-[10px] md:text-xs uppercase tracking-[0.3em] mt-2 font-sans ${theme === 'light' ? 'text-stone-400' : 'text-stone-300'}`}>
                      Hjertets Intelligens
                    </p>
                  </div>
                </div>
              )}

              {layout === 'vertical' && (
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-20 h-20">
                    <img 
                      src={activeSymbolUrl} 
                      alt="Mette Brudvig Symbol" 
                      className={`w-full h-full object-contain mix-blend-multiply ${theme !== 'light' ? 'brightness-110' : ''}`}
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <h2 className="text-3xl md:text-4xl font-serif tracking-tight leading-none">
                      Mette Brudvig
                    </h2>
                    <p className={`text-[10px] md:text-xs uppercase tracking-[0.3em] mt-2.5 font-sans ${theme === 'light' ? 'text-stone-400' : 'text-stone-300'}`}>
                      Hjertets Intelligens
                    </p>
                  </div>
                </div>
              )}

              {layout === 'symbol' && (
                <div className="w-28 h-28 flex items-center justify-center">
                  <img 
                    src={activeSymbolUrl} 
                    alt="Mette Brudvig Symbol Only" 
                    className={`w-full h-full object-contain mix-blend-multiply ${theme !== 'light' ? 'brightness-110' : ''}`}
                  />
                </div>
              )}
            </div>

            {/* Canvas Footer Actions */}
            <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-4 mt-12 pt-6 border-t border-stone-200/60">
              <div className="text-xs text-stone-500 font-light">
                <span className="font-semibold">Aktiv variant:</span> {symbols[selectedSymbol].name}
              </div>
              <div className="flex space-x-3">
                <a 
                  href={activeSymbolUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-stone-900 text-white hover:bg-stone-800 px-5 py-2.5 rounded-lg text-xs font-semibold tracking-wider uppercase transition-all shadow-md hover:shadow-lg"
                >
                  <Download size={14} />
                  <span>Hent Højopløselig</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Real-world Mockups */}
      <section className="py-16 px-6 max-w-7xl mx-auto space-y-12">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-serif text-stone-900 mb-4">Logoet i den virkelige verden</h2>
          <p className="text-stone-600 font-light text-sm">Se, hvordan dit nye logo og brand-symbol tager sig ud på forskellige materialer og platforme.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Mockup 1: Business Card */}
          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden shadow-sm flex flex-col">
            <div className="bg-stone-900 h-52 flex items-center justify-center p-8 relative overflow-hidden">
              {/* Card Front */}
              <div className="bg-white text-stone-900 w-72 h-40 rounded shadow-2xl p-6 flex flex-col justify-between border border-stone-100">
                <div className="flex items-center space-x-2">
                  <img src={symbols.gold.url} alt="Logo" className="w-6 h-6 object-contain mix-blend-multiply" />
                  <span className="font-serif text-xs font-bold tracking-tight">Mette Brudvig</span>
                </div>
                <div className="text-[8px] text-stone-500 space-y-0.5 font-light">
                  <p className="font-serif text-stone-800 font-semibold text-[9px]">Mette Brudvig</p>
                  <p className="italic mb-1">Hjertets Intelligens & Clairvoyance</p>
                  <p>mette@mettebrudvig.dk</p>
                  <p>www.mettebrudvig.dk</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-2">
              <h4 className="font-serif font-bold text-stone-900">Elegante Visitkort</h4>
              <p className="text-stone-500 font-light text-xs leading-relaxed">
                Et stilrent, hvidt visitkort med guld-symbolet og dit navn i fokus efterlader et professionelt og spirituelt indtryk.
              </p>
            </div>
          </div>

          {/* Mockup 2: Instagram Profile */}
          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden shadow-sm flex flex-col">
            <div className="bg-stone-50 h-52 flex items-center justify-center p-8">
              <div className="bg-white border border-stone-200 w-64 rounded-xl p-4 shadow-lg space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full ring-2 ring-amber-400 p-0.5 overflow-hidden bg-stone-50 flex items-center justify-center">
                    <img src={symbols.gold.url} alt="Insta Profile" className="w-full h-full object-contain mix-blend-multiply" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-900">mettebrudvig</p>
                    <p className="text-[10px] text-stone-500">Mette Brudvig</p>
                  </div>
                </div>
                <div className="text-[10px] text-stone-600 font-light leading-snug">
                  <p className="font-semibold text-stone-800">Mette Brudvig • Hjertets Intelligens</p>
                  <p>✨ Personlig udvikling & clairvoyance</p>
                  <p>🎙️ Podcast: Forbundet i den nye tid</p>
                  <p className="text-blue-900 font-medium mt-1">mettebrudvig.dk</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-2">
              <h4 className="font-serif font-bold text-stone-900">Sociale Medier</h4>
              <p className="text-stone-500 font-light text-xs leading-relaxed">
                Det rene symbol fungerer perfekt som profilbillede på Instagram, Facebook og Spotify, hvor det skaber øjeblikkelig genkendelighed.
              </p>
            </div>
          </div>

          {/* Mockup 3: Notebook / Journal */}
          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden shadow-sm flex flex-col">
            <div className="bg-[#004f3b] h-52 flex items-center justify-center p-8 relative">
              {/* Notebook Cover */}
              <div className="bg-stone-900 text-white w-32 h-44 rounded-r-lg shadow-2xl border-l-4 border-stone-950 p-4 flex flex-col items-center justify-center space-y-4">
                <img src={symbols.gold.url} alt="Gold Symbol" className="w-10 h-10 object-contain brightness-110" />
                <div className="text-center">
                  <p className="font-serif text-[10px] tracking-wider uppercase">Mette Brudvig</p>
                  <p className="text-[6px] text-stone-400 uppercase tracking-widest mt-1">Notes</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-2">
              <h4 className="font-serif font-bold text-stone-900">Notesbøger & Merchandise</h4>
              <p className="text-stone-500 font-light text-xs leading-relaxed">
                Symbolet præget i guld på en mørk notesbog eller journal til dine workshops og foredrag giver et eksklusivt og spirituelt præg.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Color Palette Section */}
      <section className="py-16 px-6 max-w-7xl mx-auto bg-white rounded-2xl border border-stone-100 shadow-sm space-y-12">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-serif text-stone-900 mb-4">Brandets Farvepalette</h2>
          <p className="text-stone-600 font-light text-sm">Harmoniske, jordnære og spirituelle farver, der understøtter dit budskab.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {colors.map((color, idx) => (
            <div key={idx} className="border border-stone-100 rounded-xl overflow-hidden shadow-sm flex flex-col">
              <div 
                className="h-28 w-full transition-transform hover:scale-105 duration-300 relative group cursor-pointer"
                style={{ backgroundColor: color.hex }}
                onClick={() => handleCopyColor(color.hex)}
              >
                <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-semibold">
                  <span>Kopiér HEX</span>
                </div>
              </div>
              <div className="p-4 flex-grow flex flex-col justify-between space-y-3 bg-stone-50/50">
                <div>
                  <h4 className="font-serif font-bold text-stone-900 text-sm">{color.name}</h4>
                  <p className="text-[11px] text-stone-500 font-mono mt-1">{color.hex}</p>
                  <p className="text-[10px] text-stone-400 font-mono">RGB: {color.rgb}</p>
                  <p className="text-[10px] text-stone-400 font-mono">CMYK: {color.cmyk}</p>
                </div>
                <div className="pt-2 border-t border-stone-200/60 text-[11px] text-stone-600 font-light">
                  <span className="font-semibold block text-stone-700">Anvendelse:</span>
                  {color.use}
                </div>
              </div>
            </div>
          ))}
        </div>

        {copiedColor && (
          <div className="fixed bottom-8 right-8 bg-stone-900 text-white px-4 py-2.5 rounded-lg shadow-xl text-xs font-semibold flex items-center space-x-2 animate-bounce z-50">
            <Check size={14} className="text-emerald-400" />
            <span>Kopieret: {copiedColor}</span>
          </div>
        )}
      </section>

      {/* Brand Kit Download Center */}
      <section className="py-16 px-6 max-w-7xl mx-auto bg-stone-900 text-white rounded-2xl shadow-xl overflow-hidden relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#004f3b]/20 rounded-full blur-3xl -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#c5a059]/10 rounded-full blur-3xl -ml-20 -mb-20"></div>

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center max-w-3xl mx-auto space-y-6 mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-amber-400 font-bold">Download Center</span>
            <h2 className="text-3xl md:text-5xl font-serif tracking-tight">Hent dit komplette Brand Kit</h2>
            <p className="text-stone-300 font-light text-sm md:text-base leading-relaxed">
              Her kan du downloade alle logo-aktiver i høj opløsning (PNG med gennemsigtig baggrund) samt en interaktiv, printbar Brand Guide.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button 
                onClick={handleDownloadAll}
                className="flex items-center space-x-3 bg-amber-500 hover:bg-amber-400 text-stone-950 px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold transition-all shadow-lg hover:scale-[1.02] cursor-pointer"
              >
                <Download size={16} />
                <span>Download alle logoer (PNG)</span>
              </button>
              
              <button 
                onClick={handleExportBrandGuide}
                className="flex items-center space-x-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold transition-all hover:scale-[1.02] cursor-pointer"
              >
                <BookOpen size={16} className="text-amber-400" />
                <span>Hent Brand Guide (HTML)</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Asset 1: Guld Logo Horisontalt */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex flex-col justify-between space-y-6 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="h-32 bg-white/5 rounded-lg p-4 flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image.png" 
                    alt="Guld Logo Horisontalt" 
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-white text-base">Guld Logo (Horisontalt)</h4>
                  <p className="text-xs text-stone-400 mt-1">Højopløselig PNG • Transparent</p>
                </div>
              </div>
              <a 
                href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/image.png" 
                download="mette-brudvig-logo-guld-horisontalt.png"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold tracking-wider uppercase text-center transition-all block"
              >
                Download
              </a>
            </div>

            {/* Asset 2: Koksgrå Logo Horisontalt */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex flex-col justify-between space-y-6 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="h-32 bg-white/5 rounded-lg p-4 flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ddf45d18-3162-4a49-b537-5eba838b3e4f.png" 
                    alt="Koksgrå Logo Horisontalt" 
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-white text-base">Koksgrå Logo (Horisontalt)</h4>
                  <p className="text-xs text-stone-400 mt-1">Højopløselig PNG • Transparent</p>
                </div>
              </div>
              <a 
                href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/ddf45d18-3162-4a49-b537-5eba838b3e4f.png" 
                download="mette-brudvig-logo-koksgraa-horisontalt.png"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold tracking-wider uppercase text-center transition-all block"
              >
                Download
              </a>
            </div>

            {/* Asset 3: Guld Symbol */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex flex-col justify-between space-y-6 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="h-32 bg-white/5 rounded-lg p-4 flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/8b5bf536-c411-474d-bbd3-0d9bb5bbc475.png" 
                    alt="Guld Symbol" 
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-white text-base">Guld Symbol (Lotus)</h4>
                  <p className="text-xs text-stone-400 mt-1">Højopløselig PNG • Transparent</p>
                </div>
              </div>
              <a 
                href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/8b5bf536-c411-474d-bbd3-0d9bb5bbc475.png" 
                download="mette-brudvig-symbol-guld.png"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold tracking-wider uppercase text-center transition-all block"
              >
                Download
              </a>
            </div>

            {/* Asset 4: Koksgrå Symbol */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex flex-col justify-between space-y-6 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="h-32 bg-white/5 rounded-lg p-4 flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f961e663-836a-4a16-bedc-b6b4579575c7.png" 
                    alt="Koksgrå Symbol" 
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-white text-base">Koksgrå Symbol (Lotus)</h4>
                  <p className="text-xs text-stone-400 mt-1">Højopløselig PNG • Transparent</p>
                </div>
              </div>
              <a 
                href="https://assets.auxodev.dk/assets/e64caba9-58c8-4d09-993b-45c3c9f29d0e/f961e663-836a-4a16-bedc-b6b4579575c7.png" 
                download="mette-brudvig-symbol-koksgraa.png"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold tracking-wider uppercase text-center transition-all block"
              >
                Download
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Guide & Next Steps */}
      <section className="py-16 px-6 max-w-4xl mx-auto text-center space-y-8">
        <h2 className="text-3xl font-serif text-stone-900">Hvordan bruger jeg logoet?</h2>
        <div className="text-left bg-stone-50 p-8 rounded-xl border border-stone-100 space-y-6 text-stone-600 font-light text-sm leading-relaxed">
          <p>
            Dit nye logo er designet til at være utroligt alsidigt. Her er et par retningslinjer for at få det bedste resultat:
          </p>
          <ul className="list-disc pl-5 space-y-3">
            <li>
              <strong className="text-stone-800">Hjemmeside:</strong> Brug det horisontale logo i headeren (allerede implementeret!). Det sikrer, at dit navn er letlæseligt, mens guld-symbolet tilføjer det spirituelle touch.
            </li>
            <li>
              <strong className="text-stone-800">Tryksager (Visitkort, foldere):</strong> Brug guld-symbolet på en mørkegrøn eller koksgrå baggrund for en eksklusiv følelse, eller det koksgrå symbol på en lys baggrund for et helt rent og minimalistisk udtryk.
            </li>
            <li>
              <strong className="text-stone-800">Sociale Medier:</strong> Brug "Kun Symbol"-varianten som dit profilbillede. Det runde format passer perfekt ind i Instagrams og Spotifys cirkulære rammer.
            </li>
          </ul>
        </div>
        <div className="pt-4">
          <a 
            href="mailto:mette@mettebrudvig.dk" 
            className="inline-flex items-center space-x-3 bg-[#004f3b] text-white px-8 py-4 uppercase text-xs tracking-[0.2em] font-bold hover:bg-[#004f3b] transition-all shadow-md"
          >
            <span>Har du spørgsmål? Skriv til mig</span>
            <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </div>
  );
}
